import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Skeleton, Snackbar, Alert, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SnackbarProvider } from 'notistack';
import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';
import { Auth0ProviderWithNavigate } from './providers/Auth0Provider';
import { FeedbackCapture } from './components/feedback/FeedbackCapture';
import { ProtectedRoute } from './components/Auth/ProtectedRoute';
import { ClinicModuleRoute } from './components/Auth/ClinicModuleRoute';
import { SimpleShippingRoute } from './components/Auth/SimpleShippingRoute';
import { setAuth0Client } from './services/api';
import { MainLayout } from './components/Layout/MainLayout';
import { useVersionCheck } from './hooks/useVersionCheck';
import { useDefaultLandingPage } from './hooks/useDefaultLandingPage';
import { SessionTimeoutManager } from './components/session/SessionTimeoutManager';
import { TestingOverlay } from './components/testing-center/TestingOverlay';
import { TESTING_CENTER_BUILD_ENABLED } from './testing-center/environment';
import Login from './pages/Login';
import Unauthorized from './pages/Unauthorized';
import NotFound from './pages/NotFound';
import Callback from './pages/Callback';
import { useAppTheme } from './theme/useAppTheme';
import { usePharmacyStore } from './stores/pharmacyStore';
import { useSettingsStore } from './stores/settingsStore';
import { ChatwootProvider } from './components/Support/ChatwootProvider';
import { RouteTracker } from './components/Debug/RouteTracker';
import { LandingNotice, landingNoticeState } from './components/Auth/LandingNotice';
import { SidebarProvider } from './contexts/SidebarContext';
import { GlobalSearchProvider } from './components/GlobalSearch';
import { GlobalPatientSearchProvider } from './components/PatientSearch/GlobalPatientSearchProvider';
import { QueryProvider } from './providers/QueryProvider';
import './App.css';
import './styles/animations.css';

// Redirect for a path that was renamed but is still linked from bookmarks and
// saved URLs. Unlike a bare <Navigate>, this carries the query string across —
// the billing queue's deep links (?tab=…&search=…) are inert without it.
const LegacyRouteRedirect: React.FC<{ to: string }> = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
};

// Every lazy-loaded page is created once, here, at module scope. `React.lazy`
// inside render makes a new component type on every App render, so React
// remounts the page: Order Detail refetched itself every couple of seconds and
// closed any dialog open on it (found while live-testing PRX-113's Invoice
// patient dialog); PRX-114 hoisted the rest. Add new pages to this list — the
// drift guard in App.lazy.drift.spec.ts fails on a lazy call inside a function.
const OrderDetailPage = React.lazy(() => import('./pages/orders/OrderDetail'));

// Two different pages are both called SystemLogs: ./pages/SystemLogs serves
// /system-logs, ./pages/system/SystemLogs serves /system/logs.
const DriverKiosk = React.lazy(() => import('./pages/kiosk/DriverKiosk'));
const TestAuth = React.lazy(() => import('./pages/TestAuth'));
const LabelPrinterTest = React.lazy(() => import('./pages/test/LabelPrinterTest'));
const ThermalLabelTest = React.lazy(() => import('./pages/test/ThermalLabelTest'));
const AdministrationHub = React.lazy(() => import('./pages/admin/AdministrationHub'));
const TopLevelSystemLogs = React.lazy(() => import('./pages/SystemLogs'));
const DeliveryTracking = React.lazy(() => import('./pages/DeliveryTracking'));
const OrderManagement = React.lazy(() => import('./pages/OrderManagement'));
const UserManagement = React.lazy(() => import('./pages/admin/users/UserManagement'));
const DrugConfiguration = React.lazy(() => import('./pages/admin/drugs/DrugConfiguration'));
const OrderStageOverride = React.lazy(() => import('./pages/admin/orders/OrderStageOverride'));
const ClinicShipmentStatusOverride = React.lazy(() => import('./pages/admin/clinic-shipments/ClinicShipmentStatusOverride'));
const SMSTemplatesPage = React.lazy(() => import('./pages/admin/sms-templates/SMSTemplatesPage'));
const SMSAuditPage = React.lazy(() => import('./pages/admin/sms-audit/SMSAuditPage'));
const GsWebhookLogPage = React.lazy(() => import('./pages/admin/gs-webhook-log/GsWebhookLogPage'));
const PrescriptionTemplatesPage = React.lazy(() => import('./pages/admin/prescription-templates/PrescriptionTemplatesPage'));
const DrugPicturesPage = React.lazy(() => import('./pages/admin/drug-pictures/DrugPicturesPage'));
const BarcodeAuditPage = React.lazy(() => import('./pages/admin/barcode-audit/BarcodeAuditPage'));
const SystemJobsPage = React.lazy(() => import('./pages/admin/system-jobs/SystemJobsPage'));
const DeliveryRemediation = React.lazy(() => import('./pages/admin/delivery-remediation/DeliveryRemediation'));
const PatientMerge = React.lazy(() => import('./pages/admin/patient-merge/PatientMerge'));
const DrugSummaryRemediation = React.lazy(() => import('./pages/admin/drug-summary-remediation/DrugSummaryRemediation'));
const NEXIUpdateRulesPage = React.lazy(() => import('./pages/admin/nexi-rules/NEXIUpdateRulesPage'));
const ScriptImageCachePage = React.lazy(() => import('./pages/admin/script-image-cache/ScriptImageCachePage'));
const OrderAuditPage = React.lazy(() => import('./pages/admin/orders/OrderAuditPage'));
const AuditCoverage = React.lazy(() => import('./pages/admin/AuditCoverage'));
const DescartesSyncIssuesPage = React.lazy(() => import('./pages/admin/descartes-sync/DescartesSyncIssuesPage'));
const DrugCatalog = React.lazy(() => import('./pages/drug-catalog/DrugCatalog').then(m => ({ default: m.DrugCatalog })));
const RolesPermissions = React.lazy(() => import('./pages/admin/roles/RolesPermissions'));
const BillingQueueCleanup = React.lazy(() => import('./pages/admin/billing-cleanup/BillingQueueCleanup'));
const GeneralSettings = React.lazy(() => import('./pages/admin/settings/GeneralSettings'));
const ZplTestPage = React.lazy(() => import('./pages/admin/settings/ZplTestPage'));
const PharmacyCouriersPage = React.lazy(() => import('./pages/admin/settings/couriers/PharmacyCouriersPage'));
const ChronicMedicationsPage = React.lazy(() => import('./pages/admin/settings/chronic-medications/ChronicMedicationsPage'));
const BarcodeDebuggerPage = React.lazy(() => import('./pages/admin/barcode-debugger/BarcodeDebuggerPage'));
const SystemConfiguration = React.lazy(() => import('./pages/admin/system/SystemConfiguration'));
const EmailSettingsPage = React.lazy(() => import('./pages/admin/system/EmailSettingsPage'));
const SubscriptionHistoryPage = React.lazy(() => import('./pages/subscription/SubscriptionHistoryPage'));
const SubscriptionManagementQueue = React.lazy(() => import('./pages/order-fulfillment/SubscriptionManagementQueue'));
const SubscriptionTodoReport = React.lazy(() => import('./components/subscription/SubscriptionTodoReport'));
const UniversalOrderSearch = React.lazy(() => import('./pages/universal/UniversalOrderSearch'));
const Patients = React.lazy(() => import('./pages/Patients'));
const Patient360Refactored = React.lazy(() => import('./pages/patient/Patient360/Patient360Refactored'));
const PatientBillingProfile = React.lazy(() => import('./pages/billing/PatientBillingProfile/PatientBillingProfile'));
const Prescriptions = React.lazy(() => import('./pages/Prescriptions'));
const ClinicalReviewQueue = React.lazy(() => import('./pages/pharmacy/ClinicalReviewQueue'));
const PackagingPage = React.lazy(() => import('./pages/pharmacy/PackagingPage'));
const TroubleshootingQueue = React.lazy(() => import('./pages/order-fulfillment/archived/TroubleshootingQueue'));
const SubscriptionBillingQueue = React.lazy(() => import('./pages/billing/SubscriptionBillingQueue'));
const RefillReviewDashboard = React.lazy(() => import('./pages/refills/RefillReviewDashboard'));
const HITLReviewQueueComplete = React.lazy(() => import('./pages/refills/HITLReviewQueueComplete'));
const DocumentProcessingCenter = React.lazy(() => import('./pages/dataIntake/DocumentProcessingCenter'));
const CreatePatientPage = React.lazy(() => import('./pages/dataIntake/CreatePatientPage'));
const PrescriptionIntakePage = React.lazy(() => import('./pages/dataIntake/PrescriptionIntakePage'));
const DocumentUpload = React.lazy(() => import('./pages/dataIntake/DocumentUpload'));
const DeliveryCenter = React.lazy(() => import('./pages/delivery/DeliveryCenter'));
const DeliveryCalendar = React.lazy(() => import('./pages/delivery/DeliveryCalendar'));
const DriverManagement = React.lazy(() => import('./pages/driver-management/DriverManagement'));
const BagHistory = React.lazy(() => import('./pages/driver-management/BagHistory'));
const DriverCheckoutHistory = React.lazy(() => import('./pages/driver-management/DriverCheckoutHistory'));
const TerritoryManagement = React.lazy(() => import('./pages/delivery/TerritoryManagement'));
const CreateShipmentPage = React.lazy(() => import('./pages/delivery/CreateShipmentPage'));
const CommunicationCenter = React.lazy(() => import('./pages/communication/CommunicationCenter').then(module => ({ default: module.CommunicationCenter })));
const KrollFaxesPage = React.lazy(() => import('./pages/kroll-faxes/KrollFaxesPage'));
const TemplateLibrary = React.lazy(() => import('./pages/communication/TemplateLibrary'));
const CommunicationCenterEmbeddable = React.lazy(() => import('./pages/communication/CommunicationCenterEmbeddable'));
const ReturnToStockWorkflow = React.lazy(() => import('./pages/orders/ReturnToStockWorkflow'));
const OperationsDashboard = React.lazy(() => import('./pages/order-fulfillment/OperationsDashboard'));
const PhoxEntryView = React.lazy(() => import('./pages/order-fulfillment/PhoxEntryView'));
// PRX-143: the PHOX Entry intake queue (requests pushed from the Communication Center).
const PhoxEntryQueuePage = React.lazy(() => import('./pages/order-fulfillment/phox-entry-queue/PhoxEntryQueuePage'));
// PRX-129: the PSP queue (specialty drug files and prior authorization).
const PspQueuePage = React.lazy(() => import('./pages/psp/PspQueuePage'));
const NexiBookingView = React.lazy(() => import('./pages/nexi/NexiBookingView'));
const NexiDraftQueue = React.lazy(() => import('./pages/nexi/NexiDraftQueue'));
const NexiVisitQueue = React.lazy(() => import('./pages/nexi/NexiVisitQueue'));
const NexiVisitsPlanningView = React.lazy(() => import('./pages/nexi/NexiVisitsPlanningView'));
const IVBillingPage = React.lazy(() => import('./pages/nexi/IVBillingPage'));
const CancellationAlertsPage = React.lazy(() => import('./pages/nexi/CancellationAlertsPage'));
const NexiWebhooksPage = React.lazy(() => import('./pages/nexi/NexiWebhooksPage'));
const OrderTrackingDashboard = React.lazy(() => import('./pages/order-fulfillment/archived/OrderTrackingDashboard'));
const BillingQueue = React.lazy(() => import('./pages/order-fulfillment/billing/BillingQueue'));
const ScheduleDeliveryQueue = React.lazy(() => import('./pages/order-fulfillment/ScheduleDeliveryQueue'));
const ClinicalVerificationQueue = React.lazy(() => import('./pages/order-fulfillment/ClinicalVerificationQueue'));
const PackagingQueue = React.lazy(() => import('./pages/order-fulfillment/PackagingQueue'));
const ProductVerificationQueue = React.lazy(() => import('./pages/order-fulfillment/ProductVerificationQueue'));
const ReadyToShipQueue = React.lazy(() => import('./pages/order-fulfillment/ReadyToShipQueue'));
const StagingArea = React.lazy(() => import('./pages/order-fulfillment/StagingArea'));
const ShippingPage = React.lazy(() => import('./pages/order-fulfillment/shipping/ShippingPage'));
const DeliveryStatusQueue = React.lazy(() => import('./pages/order-fulfillment/DeliveryStatusQueue'));
const AlertStatusQueue = React.lazy(() => import('./pages/order-fulfillment/AlertStatusQueue'));
const OrderPipelineTracker = React.lazy(() => import('./components/order-fulfillment/OrderPipelineTracker'));
const AllOrdersView = React.lazy(() => import('./pages/order-fulfillment/AllOrdersView'));
const TasksManagement = React.lazy(() => import('./pages/TasksManagement'));
// PRX-104: the pharmacy-wide Blister pack page.
const BlisterPackPage = React.lazy(() => import('./pages/blister-pack/BlisterPackPage'));
// PRX-139: the Calendar (appointments, MedsCheck booking).
const CalendarPage = React.lazy(() => import('./pages/calendar/CalendarPage'));
// PRX-134: Doctors directory and Prescriber clinics.
const DoctorsPage = React.lazy(() => import('./pages/prescribers/DoctorsPage'));
const DoctorDetailPage = React.lazy(() => import('./pages/prescribers/DoctorDetailPage'));
const PrescriberClinicsPage = React.lazy(() => import('./pages/prescribers/PrescriberClinicsPage'));
const PrescriberClinicDetailPage = React.lazy(() => import('./pages/prescribers/PrescriberClinicDetailPage'));
// PRX-140: the time clock and payroll.
const TimeClockPage = React.lazy(() => import('./pages/time-clock/TimeClockPage'));
const Profile = React.lazy(() => import('./pages/Profile'));
const HelpCenterPage = React.lazy(() => import('./pages/help/HelpCenterPage'));
// Testing Center exists only in builds with VITE_TESTING_CENTER_ENABLED=true: with the flag off
// the import is dead code, so its chunk (guides, routeHints, test-suites.json) is never emitted (PRX-121).
const TestingCenterPage = TESTING_CENTER_BUILD_ENABLED
  ? React.lazy(() => import('./pages/testing-center/TestingCenterPage'))
  : null;
const PatientComplaintFormPage = React.lazy(() => import('./pages/quality-management/PatientComplaintFormPage'));
const OrderLifecycleGuide = React.lazy(() => import('./pages/help/OrderLifecycleGuide'));
const ClinicManagement = React.lazy(() => import('./pages/ClinicManagement'));
const IVComplianceDashboard = React.lazy(() => import('./pages/clinic-management/IVComplianceDashboard'));
const ClinicAdHocDates = React.lazy(() => import('./pages/ClinicAdHocDates'));
const ClinicListView = React.lazy(() => import('./pages/clinics/ClinicListView'));
const ClinicDeliveryCalendarPage = React.lazy(() => import('./pages/clinics/ClinicDeliveryCalendarPage'));
const ClinicShipmentDetailView = React.lazy(() => import('./pages/clinic-shipments/ClinicShipmentDetailView'));
const PhoxEntryIV = React.lazy(() => import('./pages/iv/PhoxEntryIV'));
const UnlinkedPatientsView = React.lazy(() => import('./pages/iv/UnlinkedPatientsView'));
const ExcludedPatientsPage = React.lazy(() => import('./pages/iv/ExcludedPatientsPage'));
const IVOrderHistory = React.lazy(() => import('./pages/iv/IVOrderHistory'));
const ClinicShipmentQueue = React.lazy(() => import('./pages/clinic-shipments/ClinicShipmentQueue'));
const ClinicDeliveryCalendar = React.lazy(() => import('./pages/clinic-shipments/ClinicDeliveryCalendar'));
const ClinicCalendarView = React.lazy(() => import('./pages/clinic-shipments/ClinicCalendarView'));
const ProcessedOrdersPage = React.lazy(() => import('./pages/clinic-shipments/ProcessedOrdersPage'));
const SystemLogs = React.lazy(() => import('./pages/system/SystemLogs'));
const SystemLogsSummary = React.lazy(() => import('./pages/system/SystemLogsSummary'));
const UserActivityReport = React.lazy(() => import('./pages/system/UserActivityReport'));
const PHIAccessLogs = React.lazy(() => import('./pages/system/PHIAccessLogs'));
const ReimbursementTracker = React.lazy(() => import('./pages/crm/reimbursements/ReimbursementTracker'));
const DescartesDashboard = React.lazy(() => import('./pages/DescartesDashboard'));
const ReportsHub = React.lazy(() => import('./pages/reports/ReportsHub'));
const PreBillReport = React.lazy(() => import('./pages/reports/PreBillReport'));
const IVEventAuditReport = React.lazy(() => import('./pages/reports/IVEventAuditReport'));
const DeliveryMethodReport = React.lazy(() => import('./pages/reports/DeliveryMethodReport'));
const ManagementPerformanceDashboard = React.lazy(() => import('./pages/reports/ManagementPerformanceDashboard'));
const KrollReconciliationReport = React.lazy(() => import('./pages/reports/KrollReconciliationReport'));
const UserProductivityReport = React.lazy(() => import('./pages/reports/UserProductivityReport'));
const DrugRecallReport = React.lazy(() => import('./pages/reports/DrugRecallReport'));
const PatientCohortReport = React.lazy(() => import('./pages/reports/patient-cohorts/PatientCohortReport'));
const ImprovementReport = React.lazy(() => import('./pages/reports/improvement/ImprovementReport'));
const WebhookLogs = React.lazy(() => import('./pages/descartes/WebhookLogs'));

// Lightweight skeleton fallback for Suspense boundaries — matches queue page layout
const SuspenseFallback = () => (
  <Box sx={{ p: 3 }}>
    <Skeleton variant="rectangular" height={48} sx={{ mb: 2, borderRadius: 1 }} />
    <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
  </Box>
);

// Full-page skeleton shown while Auth0 is resolving, and (PRX-40) while the
// "/" redirect is waiting on the user's preferences to load — reused so both
// cases render the same placeholder instead of a blank page.
const AuthLoadingSkeleton = () => (
  <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
    {/* Skeleton topbar */}
    <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, height: 64, bgcolor: 'background.paper', zIndex: 1100, boxShadow: 1 }} />
    {/* Skeleton sidebar */}
    <Box sx={{ width: 280, minHeight: '100vh', bgcolor: 'background.paper', borderRight: '1px solid', borderColor: 'divider', pt: '64px', flexShrink: 0 }}>
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={40} sx={{ mb: 2, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={32} sx={{ mb: 1, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={32} sx={{ mb: 1, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={32} sx={{ mb: 1, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={32} sx={{ mb: 1, borderRadius: 1 }} />
      </Box>
    </Box>
    {/* Skeleton content */}
    <Box sx={{ flexGrow: 1, pt: '104px', px: 3 }}>
      <Skeleton variant="rectangular" height={48} sx={{ mb: 2, borderRadius: 1 }} />
      <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
    </Box>
  </Box>
);

function AppContent() {
  const { isLoading, isAuthenticated, getAccessTokenSilently, user } = useAuth0();
  const setUserId = usePharmacyStore((state) => state.setUserId);

  // PRX-40: reactive landing-page redirect — waits for the user's saved
  // preference to load instead of a one-shot localStorage read that never
  // revisits itself once the real preferences arrive. PRX-124: then the
  // role's page at the active pharmacy; `landingNotice` says why it fell
  // back to the dashboard when the role's page isn't open to the user.
  const {
    path: defaultLandingPage,
    ready: defaultLandingPageReady,
    notice: landingNotice,
  } = useDefaultLandingPage();

  // Check for new deployments every 5 minutes. Do NOT auto-reload — that
  // blanks the page and kills in-progress work (composing a message, mid-edit
  // in a dialog, filter state). Instead show a non-blocking snackbar and let
  // the user pick when to refresh.
  const { isUpdateAvailable, forceReload } = useVersionCheck({ autoUpdate: false });

  // Let the user dismiss the "new version" notice so it never blocks a mid-dialog
  // action (it used to sit bottom-right ON TOP of Confirm buttons with no way to
  // close it). Dismissal is local-only; the update still applies on the next reload.
  const [updateDismissed, setUpdateDismissed] = useState(false);

  // Set up Auth0 client and initialize pharmacy data when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Set up Auth0 client for axios interceptor
    const auth0Client = {
      getTokenSilently: async (options?: any) => {
        try {
          return await getAccessTokenSilently(options);
        } catch (error: any) {
          console.error('Failed to get access token:', error);

          // If missing refresh token, try without cache
          if (error.message?.includes('Missing Refresh Token')) {
            try {
              return await getAccessTokenSilently({
                ...options,
                cacheMode: 'off'
              });
            } catch (retryError) {
              console.error('Retry without cache also failed:', retryError);
              throw retryError;
            }
          }
          throw error;
        }
      },
      loginWithRedirect: (options?: any) => {
        window.location.href = '/login';
      },
    } as any;

    setAuth0Client(auth0Client);

    // Now that Auth0 client is set up, initialize pharmacy data
    if (user?.sub) {
      setUserId(user.sub);

      // Initialize pharmacy data after authentication and auth client setup
      const initializePharmacy = async () => {
        const { fetchPharmacies } = usePharmacyStore.getState();

        // Fetch all available pharmacies. fetchPharmacies() already resolves the
        // active pharmacy internally (it calls getActivePharmacy() at the end),
        // so we do NOT call getActivePharmacy() again here — that was a redundant
        // second GET /api/pharmacies/active on every boot.
        // (The previous 100ms setTimeout was also removed: setAuth0Client() above
        // is synchronous and the axios interceptor resolves the token lazily per
        // request, so the delay guarded nothing.)
        await fetchPharmacies();

        // Fetch system settings for hooks like useIdleTimeout. Kept serial after
        // pharmacy resolution so the settings request carries the correct
        // x-pharmacy-id context (multi-tenant isolation).
        const { fetchSettings } = useSettingsStore.getState();
        await fetchSettings();
      };

      initializePharmacy().catch((error) => {
        console.error('Failed to initialize pharmacy:', error);
      });
      
      // Set Sentry user context
      Sentry.setUser({
        id: user.sub,
        email: user.email,
        username: user.name || user.nickname,
      });
      
      // Set additional context
      Sentry.setContext('pharmacy', {
        pharmacyId: usePharmacyStore.getState().selectedPharmacyId,
        pharmacyName: usePharmacyStore.getState().activePharmacy?.name,
      });
    } else {
      // Clear Sentry user context on logout
      Sentry.setUser(null);
    }
  }, [isAuthenticated, user?.sub, user?.email, user?.name, user?.nickname, setUserId]);

  if (isLoading) {
    return <AuthLoadingSkeleton />;
  }

  return (
    <ChatwootProvider>
      <RouteTracker />
      <LandingNotice />
      {/* Session timeout - isolated component to avoid per-second re-renders of entire app */}
      {isAuthenticated && <SessionTimeoutManager />}
      {/* Testing Center floating session overlay — survives navigation (Zustand
          session pointer + server-shared results). Renders nothing unless a
          testing session is active. */}
      {isAuthenticated && <TestingOverlay />}
      {/* New-build notice — manual reload only. Never auto-reload (wipes unsaved work).
          Anchored bottom-LEFT and dismissible so it never sits on top of a dialog's
          Confirm buttons (which live bottom-right) and can always be cleared. */}
      <Snackbar
        open={isUpdateAvailable && !updateDismissed}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{ zIndex: (t) => t.zIndex.snackbar + 1 }}
      >
        <Alert
          severity="info"
          variant="filled"
          action={
            <>
              <Button color="inherit" size="small" onClick={() => forceReload()}>
                Reload
              </Button>
              <IconButton
                color="inherit"
                size="small"
                aria-label="Dismiss update notice"
                onClick={() => setUpdateDismissed(true)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          }
        >
          A new version is available. Reload when you're ready.
        </Alert>
      </Snackbar>
      <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/callback" element={<Callback />} />
      <Route path="/unauthorized" element={<Unauthorized />} />

      {/* Protected: Driver Checkout Kiosk (Kiosk users ONLY) */}
      <Route
        path="/kiosk"
        element={
          <ProtectedRoute allowKioskUsers={true}>
            <React.Suspense fallback={<SuspenseFallback />}>
              <DriverKiosk />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      <Route path="/test-auth" element={<React.Suspense fallback={<SuspenseFallback />}><TestAuth /></React.Suspense>} />
      <Route path="/test/label-printer" element={<React.Suspense fallback={<SuspenseFallback />}><LabelPrinterTest /></React.Suspense>} />
      <Route path="/test/thermal-label" element={<React.Suspense fallback={<SuspenseFallback />}><ThermalLabelTest /></React.Suspense>} />

      {/* Protected Routes */}





      {/* Admin Hub - Main entry point for administration */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <AdministrationHub />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Legacy route redirect */}
      <Route path="/administration" element={<Navigate to="/admin" replace />} />

      <Route
        path="/system-logs"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <TopLevelSystemLogs />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/delivery-tracking"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <DeliveryTracking />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-management"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <OrderManagement />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requiredCustomPermissions={['manageUsers']}>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <UserManagement />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/drugs"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <DrugConfiguration />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/order-stage-override"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <OrderStageOverride />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/clinic-shipment-override"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <ClinicShipmentStatusOverride />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/sms-templates"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <SMSTemplatesPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/sms-audit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <SMSAuditPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/gs-webhook-log"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <GsWebhookLogPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/prescription-templates"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <PrescriptionTemplatesPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/drug-pictures"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <DrugPicturesPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/barcode-audit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <BarcodeAuditPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/system-jobs"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <SystemJobsPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/delivery-remediation"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <DeliveryRemediation />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/patient-merge"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <PatientMerge />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/drug-summary-remediation"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <DrugSummaryRemediation />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/nexi-rules"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <NEXIUpdateRulesPage />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/script-image-cache"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<div>Loading...</div>}>
                <ScriptImageCachePage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/order-audit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <OrderAuditPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/audit-coverage"
        element={
          <ProtectedRoute requiredCustomRoles={['pharmacy_admin', 'wally_dev_admin']}>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <AuditCoverage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/descartes-sync-issues"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <DescartesSyncIssuesPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/drug-catalog"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <DrugCatalog />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/roles"
        element={
          <ProtectedRoute requiredCustomPermissions={['manageUsers']}>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <RolesPermissions />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />


      {/* Admin Billing Queue Cleanup */}
      <Route
        path="/admin/billing-cleanup"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <BillingQueueCleanup />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Admin Settings */}
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <GeneralSettings />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* ZPL Orientation Test Page */}
      <Route
        path="/admin/settings/zpl-test"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <ZplTestPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Settings → Couriers (PRX-56) */}
      <Route
        path="/admin/settings/couriers"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <PharmacyCouriersPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Settings → Chronic medication list (PRX-141) */}
      <Route
        path="/admin/settings/chronic-medications"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <ChronicMedicationsPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* 2D Barcode Debugger */}
      <Route
        path="/admin/barcode-debugger"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <BarcodeDebuggerPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Legacy route redirect */}
      <Route path="/settings" element={<Navigate to="/admin/settings" replace />} />

      <Route
        path="/admin/system-config"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <SystemConfiguration />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/email-settings"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <EmailSettingsPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/subscriptions/history/:subscriptionId"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <SubscriptionHistoryPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

            <Route
              path="/order-fulfillment/subscription-management"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <React.Suspense fallback={<SuspenseFallback />}>
                      <SubscriptionManagementQueue />
                    </React.Suspense>
                  </MainLayout>
                </ProtectedRoute>
              }
            />
      
      {/* /subscriptions/edit/:id removed (spec §5.5): the free-form subscription
          editor is gone — all edits go through the scoped recipe pathways. */}

      <Route
        path="/todo"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <SubscriptionTodoReport />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/universal-search"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <UniversalOrderSearch />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      
      
      
      <Route
        path="/patients"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<SuspenseFallback />}>
              <Patients />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* PATIENT 360 - Refactored Version (Default) */}
      <Route
        path="/patients/:patientId"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <Patient360Refactored />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/patients/:patientId/billing"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <PatientBillingProfile />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/prescriptions"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<SuspenseFallback />}>
              <Prescriptions />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      

      
      <Route
        path="/pharmacy/clinical-review-queue"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <ClinicalReviewQueue />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/pharmacy/packaging"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <PackagingPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      


      {/* Troubleshooting Queue */}
      <Route
        path="/order-fulfillment/troubleshooting"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <TroubleshootingQueue />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/billing/dashboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <SubscriptionBillingQueue />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      
      <Route
        path="/refills/review"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <RefillReviewDashboard />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* HITL Queue - Now enabled with backend implementation */}
      <Route
        path="/refills/hitl-queue"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <HITLReviewQueueComplete />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      

      
      <Route
        path="/data-intake"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <DocumentProcessingCenter />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/data-intake/create-patient"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <CreatePatientPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/data-intake/batch"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <PrescriptionIntakePage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/data-intake/upload"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <DocumentUpload />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      

      

      
      <Route
        path="/delivery"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <DeliveryCenter />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/delivery/calendar"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <DeliveryCalendar />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/driver-management"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <DriverManagement />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/bag-history"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <BagHistory />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/driver-checkout-history"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <DriverCheckoutHistory />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/delivery/territories"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <TerritoryManagement />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/delivery/create-shipment"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <CreateShipmentPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/communications"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <CommunicationCenter />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/kroll-faxes"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <KrollFaxesPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/communications/templates"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <TemplateLibrary />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/communications/embeddable"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <CommunicationCenterEmbeddable />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/orders/:pharmacyId/:orderId"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <OrderDetailPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/orders/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <OrderDetailPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      
      <Route
        path="/orders/return-to-stock/:orderId?"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<SuspenseFallback />}>
              <ReturnToStockWorkflow />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Order Fulfillment Section - 9-Stage Workflow */}
      <Route
        path="/order-fulfillment"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <OperationsDashboard />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-fulfillment/operations-dashboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <OperationsDashboard />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/order-fulfillment/Phox-entry"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <PhoxEntryView />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/psp-queue"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <PspQueuePage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-fulfillment/phox-entry-queue"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <PhoxEntryQueuePage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/phox-entry"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <PhoxEntryView />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/nexi/bookings"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <NexiBookingView />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/nexi/draft-queue"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <NexiDraftQueue />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/nexi/visit-queue"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <NexiVisitQueue />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/nexi/visits-planning"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <NexiVisitsPlanningView />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/nexi/iv-billing"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <IVBillingPage />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/nexi/cancellation-alerts"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <CancellationAlertsPage />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/nexi/webhooks"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <NexiWebhooksPage />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-fulfillment/tracking"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <OrderTrackingDashboard />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/*
        GP-1204: the billing queue was renamed to /billing-queue, but links to
        the old /billing path survived in the Patient 360 "In Troubleshooting"
        chip, the Subscription Management queue, the context notes drawer and
        global search. With no route and no catch-all, React Router rendered an
        empty outlet — a blank page with no error and no request. Those callers
        now link to /billing-queue directly; this keeps every remaining old link
        (bookmarks included) working.
      */}
      <Route
        path="/order-fulfillment/billing"
        element={<LegacyRouteRedirect to="/order-fulfillment/billing-queue" />}
      />

      <Route
        path="/order-fulfillment/billing-queue"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <BillingQueue />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-fulfillment/schedule-delivery"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <ScheduleDeliveryQueue />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/order-fulfillment/clinical-verification"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <ClinicalVerificationQueue />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/order-fulfillment/packaging"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <PackagingQueue />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/order-fulfillment/product-verification"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <ProductVerificationQueue />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/order-fulfillment/ready-to-ship"
        element={
          <ProtectedRoute>
            <MainLayout>
              {/* PRX-16: replaced by /order-fulfillment/shipping where the
                  pharmacy turned one-page shipping on. */}
              <SimpleShippingRoute requires="off">
              <React.Suspense fallback={<SuspenseFallback />}>
              <ReadyToShipQueue />
                {/* {React.createElement(React.lazy(() => import('./pages/order-fulfillment/ready-to-ship')))} */}
              </React.Suspense>
              </SimpleShippingRoute>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-fulfillment/staging-area"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SimpleShippingRoute requires="off">
              <React.Suspense fallback={<SuspenseFallback />}>
                <StagingArea />
              </React.Suspense>
              </SimpleShippingRoute>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* PRX-16: one page, two clicks. Only exists where the pharmacy turned
          one-page shipping on; its two endpoints 404 for everyone else. */}
      <Route
        path="/order-fulfillment/shipping"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SimpleShippingRoute requires="on">
                <React.Suspense fallback={<SuspenseFallback />}>
                  <ShippingPage />
                </React.Suspense>
              </SimpleShippingRoute>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-fulfillment/delivery-status"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <DeliveryStatusQueue />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-fulfillment/alert-status"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <AlertStatusQueue />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-fulfillment/pipeline-tracker"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <OrderPipelineTracker />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/order-fulfillment/all-orders"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <AllOrdersView />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      
      


      
      {/* PRX-139: the Calendar — Month, Week and Day, staff calendars, Coming up. */}
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <CalendarPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* PRX-104: the pharmacy-wide Blister pack page (Billing, SynMed). */}
      <Route
        path="/blister-pack"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <BlisterPackPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* PRX-140: the time clock and payroll. */}
      <Route
        path="/time-clock"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <TimeClockPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* PRX-134: Doctors directory and Prescriber clinics. */}
      <Route
        path="/prescribers/doctors"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <DoctorsPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/prescribers/doctors/:pmsDoctorId"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <DoctorDetailPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/prescribers/clinics"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <PrescriberClinicsPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/prescribers/clinics/:clinicId"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <PrescriberClinicDetailPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <TasksManagement />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <Profile />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <HelpCenterPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Testing Center — in-app QA testing guides with shared results.
          No requiredCustomRoles here on purpose: the admin-in-production gate
          lives inside the page (respecting the null-role graceful fallback),
          matching the /reports/management-performance convention above. */}
      {TestingCenterPage && (
        <Route
          path="/testing-center"
          element={
            <ProtectedRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <TestingCenterPage />
                </React.Suspense>
              </MainLayout>
            </ProtectedRoute>
          }
        />
      )}

      {/* Quality Management — Patient Complaint form capture (GP-207). */}
      <Route
        path="/quality-management/complaint-forms"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <PatientComplaintFormPage />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/help/order-lifecycle"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <OrderLifecycleGuide />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/clinic-management"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <ClinicManagement />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/clinic-management/iv-compliance"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <IVComplianceDashboard />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/clinic-ad-hoc-dates"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <ClinicAdHocDates />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/clinics"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <ClinicListView />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/clinics/delivery-calendar"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <ClinicDeliveryCalendarPage />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/clinic-shipments/:clinicId"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <ClinicShipmentDetailView />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/iv/phox-entry"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <PhoxEntryIV />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/iv/unlinked-patients"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <UnlinkedPatientsView />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/iv/excluded-patients"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <ExcludedPatientsPage />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/iv/order-history"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <IVOrderHistory />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/clinic-shipments"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <ClinicShipmentQueue />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/clinic-delivery-calendar"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <ClinicDeliveryCalendar />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/clinic-calendar"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <ClinicCalendarView />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/processed-orders"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <ProcessedOrdersPage />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/system/logs"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <SystemLogs />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/system/logs/summary"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <SystemLogsSummary />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/system/logs/user-activity"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <UserActivityReport />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/system/logs/phi-access"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <PHIAccessLogs />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* CRM */}
      <Route
        path="/crm/reimbursements"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <ReimbursementTracker />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/descartes"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <DescartesDashboard />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Reports */}
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <ReportsHub />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/pre-bill"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <PreBillReport />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/iv-event-audit"
        element={
          <ProtectedRoute>
            <ClinicModuleRoute>
              <MainLayout>
                <React.Suspense fallback={<SuspenseFallback />}>
                  <IVEventAuditReport />
                </React.Suspense>
              </MainLayout>
            </ClinicModuleRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/delivery-method"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <DeliveryMethodReport />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* viewReports is enforced by the backend guard; no route-level custom-permission
          gate — hasCustomPermission is false while the role is loading, which would
          lock out null-role users (convention: null role shows everything) */}
      <Route
        path="/reports/management-performance"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <ManagementPerformanceDashboard />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/kroll-reconciliation"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <KrollReconciliationReport />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/verification-productivity"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <UserProductivityReport />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/drug-recall"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <DrugRecallReport />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/patient-cohorts"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <PatientCohortReport />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/improvement"
        element={
          <ProtectedRoute requiredCustomRoles={['wally_dev_admin']}>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <ImprovementReport />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/descartes/webhook-logs"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<SuspenseFallback />}>
                <WebhookLogs />
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/"
        element={
          isAuthenticated ? (
            defaultLandingPageReady ? (
              <Navigate to={defaultLandingPage} replace state={landingNoticeState(landingNotice)} />
            ) : (
              <AuthLoadingSkeleton />
            )
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/*
        GP-1204: catch-all, so an unmatched path can never render a blank page
        again. React Router ranks by specificity, not declaration order, so this
        only wins when nothing else matches.
      */}
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <MainLayout>
              <NotFound />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      </Routes>
    </ChatwootProvider>
  );
}

function App() {
  const appTheme = useAppTheme();
  return (
    <QueryProvider>
      <ThemeProvider theme={appTheme}>
        <CssBaseline />
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          autoHideDuration={4000}
        >
          <Router>
            <Auth0ProviderWithNavigate>
              <SidebarProvider>
                <GlobalSearchProvider>
                  <GlobalPatientSearchProvider>
                    <AppContent />
                    <FeedbackCapture />
                  </GlobalPatientSearchProvider>
                </GlobalSearchProvider>
              </SidebarProvider>
            </Auth0ProviderWithNavigate>
          </Router>
        </SnackbarProvider>
      </ThemeProvider>
    </QueryProvider>
  );
}

export default App;
